/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 23/05/2025 - 01:44:49
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 23/05/2025
    * - Author          : 
    * - Modification    : 
**/
// api/transaction.js - Updated to include analysis service functions
import axios from "axios";
import { auth } from "../firebase";

// Define the base URL based on environment
const getBaseUrl = () => {
  return 'https://stripe.pv-glarecheck.com';
};

const BASE_URL = getBaseUrl();

// Type definitions (if using TypeScript)
interface CouponCheckRequest {
  couponID: string;
  originalAmount: number;
  paymentIntent_ID: string;
}

interface VoucherApplyRequest {
  voucherCode: string;
  paymentIntentId: string;
}

interface FreeOrderRequest {
  voucherCode: string;
  originalAmount: number;
  projectId: string;
  userId?: string; 
}

interface AnalysisOrderRequest {
  userId: string;
  email: string;
  userName: string;
  contactInfo?: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
  };
}

interface AnalysisOrderQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  sortBy?: string;
}

export const api = {
  // Existing payment intent function
  fetchClientSecret: async (data: {
    amount: number, 
    projectId: string,
    simulationId: string
  }) => {
    try {
      // Validate amount before sending
      if (!data.amount || data.amount <= 0) {
        throw new Error("Invalid amount. Amount must be greater than 0.");
      }
      
      // Get current user from Firebase auth
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      // Create the request payload with all necessary information
      const payload = {
        amount: data.amount,
        userId: user.uid,
        userEmail: user.email,
        projectId: data.projectId,
        simulationId: data.simulationId
      };
      
      const response = await axios.post(
        `${BASE_URL}/transaction`,
        payload
      );
      
      return response;
    } catch (error) {
      console.error("Error creating payment intent:", error);
      throw error;
    }
  },

   /**
   * Create an invoice for a payment
   * @param paymentIntentId - The ID of the successful payment intent
   * @returns Promise with invoice data
   */
   createInvoiceForPayment: async (paymentIntentId: string) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/invoices/payment/${paymentIntentId}`
      );
      return response;
    } catch (error) {
      console.error("Error creating invoice:", error);
      throw error;
    }
  },
  
  // Legacy coupon check
  fetchCouponDetails: async (data: CouponCheckRequest) => {
    try {
      // console.log("Checking coupon with data:", data);
      return await axios.post(`${BASE_URL}/get_coupon/check`, data);
    } catch (error) {
      console.error("Error checking coupon:", error);
      throw error;
    }
  },
  
  // New voucher application
  applyVoucher: async (data: VoucherApplyRequest) => {
    try {
      // console.log("Applying voucher with data:", data);
      return await axios.post(`${BASE_URL}/get_coupon/apply`, data);
    } catch (error) {
      console.error("Error applying voucher:", error);
      throw error;
    }
  },
  
  // Updated free order completion
  completeFreeOrder: async (data: FreeOrderRequest) => {
    try {
      // console.log("Processing free order with data:", data);
      return await axios.post(`${BASE_URL}/get_coupon/completefreeorder`, data);
    } catch (error) {
      console.error("Error processing free order:", error);
      throw error;
    }
  },
  
  // Add diagnostic endpoint
  getDiagnostics: async () => {
    try {
      return await axios.get(`${BASE_URL}/diagnostics`);
    } catch (error) {
      console.error("Error fetching diagnostics:", error);
      throw error;
    }
  },

  // NEW ANALYSIS SERVICE FUNCTIONS
  
  // Create analysis payment intent (€199)
  createAnalysisOrder: async (data: AnalysisOrderRequest) => {
    try {
      // console.log("Creating analysis order with data:", data);
      const response = await axios.post(`${BASE_URL}/analysis/payment`, data);
      // console.log("Analysis order created successfully:", response.data);
      return response;
    } catch (error) {
      console.error("Error creating analysis order:", error);
      throw error;
    }
  },

  // Get user's analysis orders
  getUserAnalysisOrders: async (userId: string, params?: AnalysisOrderQueryParams) => {
    try {
      // console.log("Fetching analysis orders for user:", userId);
      const response = await axios.get(`${BASE_URL}/analysis/orders/${userId}`, {
        params: params
      });
      // console.log("Analysis orders fetched successfully:", response.data);
      return response;
    } catch (error) {
      console.error("Error fetching analysis orders:", error);
      throw error;
    }
  },

  // Get specific analysis order by ID
  getAnalysisOrderById: async (orderId: string) => {
    try {
      // console.log("Fetching analysis order:", orderId);
      const response = await axios.get(`${BASE_URL}/analysis/order/${orderId}`);
      // console.log("Analysis order fetched successfully:", response.data);
      return response;
    } catch (error) {
      console.error("Error fetching analysis order:", error);
      throw error;
    }
  },

  // Update analysis order status (admin)
  updateAnalysisOrder: async (orderId: string, updateData: any) => {
    try {
      // console.log("Updating analysis order:", orderId, updateData);
      const response = await axios.patch(`${BASE_URL}/analysis/order/${orderId}`, updateData);
      // console.log("Analysis order updated successfully:", response.data);
      return response;
    } catch (error) {
      console.error("Error updating analysis order:", error);
      throw error;
    }
  },

  // Update analysis order status after payment
  updateAnalysisOrderStatus: async (orderId: string, paymentData: any) => {
    try {
      // console.log("Updating analysis order status after payment:", orderId);
      const updateData = {
        status: 'completed',
        deliveryStatus: 'not_started', // Will be updated when analysis begins
        paymentStatus: true,
        transactionId: paymentData.transactionId,
        paidAmount: paymentData.amount,
        paidAt: new Date(),
        voucherApplied: paymentData.voucherCode || null,
        discountAmount: paymentData.discountAmount || 0
      };
      
      const response = await axios.patch(`${BASE_URL}/analysis/order/${orderId}/status`, updateData);
      // console.log("Analysis order status updated successfully:", response.data);
      return response;
    } catch (error) {
      console.error("Error updating analysis order status:", error);
      throw error;
    }
  },

  // Get analysis statistics (admin)
  getAnalysisStats: async () => {
    try {
      // console.log("Fetching analysis statistics");
      const response = await axios.get(`${BASE_URL}/analysis/stats`);
      // console.log("Analysis stats fetched successfully:", response.data);
      return response;
    } catch (error) {
      console.error("Error fetching analysis stats:", error);
      throw error;
    }
  },

  // Get webhook status
  getWebhookStatus: async () => {
    try {
      return await axios.get(`${BASE_URL}/webhook/stripe/status`);
    } catch (error) {
      console.error("Error fetching webhook status:", error);
      throw error;
    }
  }
};